import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  aplicarTagsEmLote,
  createCategoria,
  createTag,
  deleteCategoria,
  deleteTag,
  fetchTags,
  fetchTagsComUso,
  setTagsDoRegistro,
  updateCategoria,
  updateTag,
  urlTagsDoRegistro,
  type RecursoTag,
  type Tag,
  type TagResumo,
  type TagsDisponiveis,
  type TagUso,
} from '@/api/tags';
import type { Escopo } from '@/components/tags/TagForms';
import { fetchEscritorios, type EscritorioResumo } from '@/api/client';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import type { CacheDeTags } from '@/hooks/useTagsNoCache';
import { useAtuacao } from '@/lib/delegacao/atuacao';

/** Listas que mostram chips: renomear, recolorir ou excluir uma tag muda todas. */
const LISTAS_COM_TAGS: readonly (readonly string[])[] = [
  queryKeys.processos.all,
  queryKeys.clientes.all,
  queryKeys.modelos.all,
  queryKeys.documentos.artefatos,
];

/**
 * Tags e categorias que a pessoa vê (as pessoais e as dos escritórios). Persistida:
 * o diálogo e o filtro funcionam offline (PLANO_TAGS D12).
 */
export function useTags() {
  const query = useQuery<TagsDisponiveis>({
    queryKey: [...queryKeys.tags.all],
    queryFn: fetchTags,
    meta: queryPersistMeta.always,
    staleTime: 5 * 60 * 1000,
  });
  const tags = useMemo(() => query.data?.items ?? [], [query.data]);
  const categorias = useMemo(() => query.data?.categorias ?? [], [query.data]);
  const equipes = useMemo(() => query.data?.equipes ?? [], [query.data]);
  const tagsPorId = useMemo(() => new Map<string, Tag>(tags.map((t) => [t.id, t])), [tags]);
  return { ...query, tags, categorias, equipes, tagsPorId };
}

/**
 * Escritórios das seções "Do escritório" e do escopo da tag nova. Mesma chave da tela
 * de modelos e do envio de documento — sem depender do AuthProvider.
 *
 * Na conta de outra pessoa (delegação), `/escritorios` é da PESSOA logada; as tags,
 * porém, são as do escritório da delegação. Ficam só os escritórios que aparecem nas
 * tags e categorias visíveis — senão o delegado veria (e criaria tag em) escritório
 * dele que não vale naquela conta.
 */
export function useEscritoriosDasTags(): EscritorioResumo[] {
  const { data } = useQuery<EscritorioResumo[]>({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
    staleTime: 5 * 60 * 1000,
  });
  const atuacao = useAtuacao();
  const { tags, categorias } = useTags();
  return useMemo(() => {
    const todos = data ?? [];
    if (!atuacao) return todos;
    const doContexto = new Set([...tags, ...categorias].map((t) => t.escritorio_id).filter(Boolean));
    return todos.filter((e) => doContexto.has(e.id));
  }, [data, atuacao, tags, categorias]);
}

/** Título da seção das tags pessoais: "Minhas", ou as do titular na conta dele. */
export function useTituloTagsPessoais(): string {
  const atuacao = useAtuacao();
  return atuacao ? `De ${atuacao.titularNome}` : 'Minhas';
}

const USO_DAS_TAGS = [...queryKeys.tags.all, 'uso'] as const;

/** Marca as listas "onde está" da tela de Tags (`[...<lista do recurso>, POR_TAG, tagId]`). */
export const POR_TAG = 'por-tag';

/**
 * Tag aplicada ou removida num registro, em qualquer tela: a contagem e as listas
 * "onde está" da tela de Tags ficam velhas.
 */
function recarregarUsoDasTags(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [...USO_DAS_TAGS] });
  queryClient.invalidateQueries({ predicate: (q) => q.queryKey.includes(POR_TAG) });
}

/**
 * Escopos em que se cria tag ou categoria ("Quem vê"): o pessoal e os escritórios do contexto.
 * ``comEquipes``: também as equipes que a pessoa vê (só a tag; a categoria não tem equipe, A12).
 */
export function useEscoposDasTags({ comEquipes = false }: { comEquipes?: boolean } = {}): Escopo[] {
  const escritorios = useEscritoriosDasTags();
  const { equipes } = useTags();
  const atuacao = useAtuacao();
  return useMemo(() => {
    const doContexto = new Map(escritorios.map((e) => [e.id, e.nome]));
    const daEquipe = comEquipes
      ? equipes
          .filter((e) => doContexto.has(e.escritorio_id))
          .map((e) => ({
            id: e.escritorio_id as string | null,
            // Com dois escritórios, a equipe diz de qual é (o nome só é único no escritório).
            nome: escritorios.length > 1 ? `${e.nome} (${doContexto.get(e.escritorio_id)})` : e.nome,
            equipeId: e.id,
          }))
      : [];
    return [
      { id: null, nome: atuacao ? `Só ${atuacao.titularNome}` : 'Só eu' },
      ...escritorios.map((e) => ({ id: e.id as string | null, nome: e.nome })),
      ...daEquipe,
    ];
  }, [escritorios, atuacao, equipes, comEquipes]);
}

/** "Quem vê" já marcado ao criar: o escritório (o primeiro), e o pessoal só sem escritório. */
export function escopoPadrao(escopos: Escopo[]): string | null {
  return escopos.find((e) => e.id)?.id ?? null;
}

/**
 * Em quantos registros cada tag está (tela de Tags). A chave fica debaixo da das tags:
 * criar, editar ou excluir tag recarrega as duas. Persistida, para a tela abrir offline
 * com o último número.
 */
export function useUsoDasTags() {
  const query = useQuery<TagsDisponiveis>({
    queryKey: [...USO_DAS_TAGS],
    queryFn: fetchTagsComUso,
    meta: queryPersistMeta.always,
    // Os chips mudam em qualquer tela: ao abrir a tela de Tags, o número é sempre refeito.
    refetchOnMount: 'always',
  });
  const usoPorId = useMemo(
    () => new Map<string, TagUso>((query.data?.items ?? []).flatMap((t) => (t.uso ? [[t.id, t.uso] as const] : []))),
    [query.data],
  );
  return { ...query, usoPorId };
}

/**
 * Criar, editar e excluir tag e categoria — só online (D12): a tag nova precisa do
 * id do servidor para ser aplicada, e a exclusão some com ela de todos os registros.
 */
export function useGestaoTags() {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const atualizar = (listasTambem: boolean) => {
    queryClient.invalidateQueries({ queryKey: [...queryKeys.tags.all] });
    if (listasTambem) {
      for (const key of LISTAS_COM_TAGS) queryClient.invalidateQueries({ queryKey: [...key] });
    }
  };
  const comErro = { onError: (err: unknown) => notifyMutationError(err, showError) };

  return {
    criarTag: useMutation({ mutationFn: createTag, onSuccess: () => atualizar(false), ...comErro }),
    editarTag: useMutation({
      mutationFn: ({ id, ...payload }: { id: string; nome?: string; cor?: string; categoria_id?: string | null }) =>
        updateTag(id, payload),
      onSuccess: () => atualizar(true),
      ...comErro,
    }),
    excluirTag: useMutation({ mutationFn: deleteTag, onSuccess: () => atualizar(true), ...comErro }),
    criarCategoria: useMutation({ mutationFn: createCategoria, onSuccess: () => atualizar(false), ...comErro }),
    editarCategoria: useMutation({
      mutationFn: ({ id, ...payload }: { id: string; nome?: string; ordem?: number }) => updateCategoria(id, payload),
      onSuccess: () => atualizar(false),
      ...comErro,
    }),
    excluirCategoria: useMutation({ mutationFn: deleteCategoria, onSuccess: () => atualizar(true), ...comErro }),
  };
}

interface VariaveisAplicar {
  id: string;
  tags: TagResumo[];
}

/** Toque mais recente por registro, no MÓDULO (o mesmo registro aparece em mais de um lugar). */
const ultimoToque = new Map<string, number>();

/**
 * Aplica ou remove tag num registro, OTIMISTA com volta — como a estrela de favorito.
 *
 * Manda sempre o CONJUNTO desejado (PUT idempotente), nunca "adicione X": dois toques
 * rápidos, ou o replay do outbox, não deixam o registro no inverso do que a tela
 * mostra. Offline, o PUT vai para o outbox num grupo por registro (FIFO).
 *
 * `cache` escreve o conjunto nos caches da tela (lista e detalhe); a resposta do
 * servidor reconcilia, e um erro devolve o conjunto anterior.
 */
export function useAplicarTags(recurso: RecursoTag, cache: CacheDeTags) {
  const { showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useOfflineMutation<TagResumo[], VariaveisAplicar>({
    mutationFn: ({ id, tags }) => setTagsDoRegistro(recurso, id, tags.map((t) => t.id)),
    outbox: {
      type: 'tags_aplicar',
      getPayload: ({ id, tags }) => ({
        method: 'PUT',
        url: urlTagsDoRegistro(recurso, id),
        body: { tag_ids: tags.map((t) => t.id) },
        grupo: `tags:${recurso}:${id}`,
      }),
      getDescription: ({ tags }) => (tags.length ? `Marcar com ${tags.map((t) => t.nome).join(', ')}` : 'Tirar as tags'),
    },
  });

  const definir = async (id: string, tags: TagResumo[], anteriores: TagResumo[]) => {
    const chave = `${recurso}:${id}`;
    const toque = (ultimoToque.get(chave) ?? 0) + 1;
    ultimoToque.set(chave, toque);
    const ehOUltimo = () => ultimoToque.get(chave) === toque;

    // Uma leitura em voo (lote, foco da janela) traria o conjunto antigo por cima.
    await cache.cancelar();
    cache.escrever(id, tags);
    try {
      const resposta = await mutation.mutateAsync({ id, tags });
      if (ehOUltimo() && Array.isArray(resposta)) cache.escrever(id, resposta);
    } catch (err) {
      if (!ehOUltimo()) return;
      cache.escrever(id, anteriores);
      notifyMutationError(err, showError);
    } finally {
      recarregarUsoDasTags(queryClient);
    }
  };

  return { definir };
}

/** Aplica ou remove tags em vários registros (D13). Offline vai para o outbox. */
export function useAplicarTagsEmLote(recurso: RecursoTag, invalidar: readonly (readonly string[])[]) {
  const { showError, showSuccess } = useNotification();
  const queryClient = useQueryClient();
  return useOfflineMutation<{ alterados: number }, { ids: string[]; adicionar?: string[]; remover?: string[] }>({
    mutationFn: (variaveis) => aplicarTagsEmLote(recurso, variaveis),
    outbox: {
      type: 'tags_aplicar',
      getPayload: (variaveis) => ({ method: 'POST', url: `/tags/${recurso}/lote`, body: variaveis }),
      getDescription: ({ ids }) => `Tags em ${ids.length} ${ids.length === 1 ? 'registro' : 'registros'}`,
    },
    invalidateKeys: invalidar.map((k) => [...k]),
    onSuccess: (_data, { ids }) => {
      recarregarUsoDasTags(queryClient);
      showSuccess(`Tags atualizadas em ${ids.length} ${ids.length === 1 ? 'registro' : 'registros'}.`);
    },
    onError: (err) => {
      recarregarUsoDasTags(queryClient);
      notifyMutationError(err, showError);
    },
  });
}
